import { useEffect, useState, useContext, useRef, forwardRef, useImperativeHandle } from 'react';
import BankInfoImageEn from '../assets/images/bank-info_en.png';
import BankInfoImageFr from '../assets/images/bank-info_fr.png';
import Modal from './Modal';
import useModal from '../hooks/useModal';
import { LanguageContext } from "../context/LanguageProvider";
import ApiConstants from '../utils/ApiConstants';
import { useAuthenticatedApiFetch } from '../hooks/useAuthenticatedApiFetch';
import { useMsal } from "@azure/msal-react";
import ErrorModal from './ErrorModal';
import { getErrorMessages } from '../utils/errorMessages';
import { useSimulation } from "../context/SimulationContext";

const PaymentEFT = forwardRef(({
  product,
  productId,
  client,
  onPaymentPageLoad,
  validationErrors = {}
}, ref) => {
  const { t, language } = useContext(LanguageContext);
  const { ERROR_TITLE, ERROR_MESSAGE } = getErrorMessages(t);

  const { instance } = useMsal();
  const authenticatedApiFetch = useAuthenticatedApiFetch(instance);
  const { isSimulation } = useSimulation();

  const getSimulationData = () => ({
    accountNumber: '00000000',
    routingNumber: '000',
    branchTransitNumber: '00000',
    dateOfWithdraw: '15',
    bankName: 'Test bank',
    branchAddress: '123 main st Belleville QC JJJ 1J1'
  });

  const {
    isModalVisible: isBankModalVisible,
    modalContent: bankModalContent,
    openModal: openBankModal,
    closeModal: closeBankModal
  } = useModal();

  const {
    isModalVisible: isErrorModalVisible,
    modalContent: errorModalContent,
    openModal: openErrorModal,
    closeModal: closeErrorModal
  } = useModal();

  // form fields
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [branchTransitNumber, setBranchTransitNumber] = useState('');
  const [dateOfWithdraw, setDateOfWithdraw] = useState('');

  // bank info returned by server
  const [bankName, setBankName] = useState('');
  const [branchAddress, setBranchAddress] = useState('');
  const [bankInfoReceived, setBankInfoReceived] = useState(false);

  // loading/submission/touched
  const [loading, setLoading] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);

  const isMounted = useRef(true);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  // notify parent when payment page loads
  useEffect(() => {
    if (onPaymentPageLoad) onPaymentPageLoad();

    if (isSimulation) {
      const { accountNumber, routingNumber, branchTransitNumber, dateOfWithdraw, bankName, branchAddress } = getSimulationData();
      setAccountNumber(accountNumber);
      setRoutingNumber(routingNumber);
      setBranchTransitNumber(branchTransitNumber);
      setDateOfWithdraw(dateOfWithdraw);
      setBankName(bankName);
      setBranchAddress(branchAddress);
      setBankInfoReceived(true);
    }

  }, [onPaymentPageLoad]);

  // fetch bank info when routing + branch are complete
  const fetchBankInfo = async () => {
    // require routing 3 digits and branch 5 digits
    if (
      routingNumber &&
      branchTransitNumber &&
      routingNumber.length === 3 &&
      branchTransitNumber.length === 5
    ) {
      try {
        const url = `${ApiConstants.BASE_URL}${ApiConstants.getBankInfo(product, productId, client)}`;
        const requestBody = {
          banking: {
            branchNumber: branchTransitNumber,
            institutionNumber: routingNumber,
          },
        };

        const response = await authenticatedApiFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        if (response?.status === 200 && response?.data) {
          const bankData = response.data;


          // Accept partial bank info: if a field is null/empty, just leave it blank.
          // No error modal for incomplete bank details.
          const name =
            language === 'Français'
              ? (bankData.nameFr || bankData.nameEn)
              : (bankData.nameEn || bankData.nameFr);

          const addr = bankData.address || {};
          const addressParts = [addr.street, addr.city, addr.province, addr.postalCode].filter(Boolean);
          const address = addressParts.join(', ');

          setBankName(name || '');
          setBranchAddress(address || '');

          // Consider bankInfoReceived true if we got any meaningful info back
          setBankInfoReceived(Boolean(name || address));
        } else {
          // no data or unexpected response
          setBankName('');
          setBranchAddress('');
          setBankInfoReceived(false);
          openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
        }
      } catch (err) {
        console.error('Error fetching bank info:', err);
        setBankName('');
        setBranchAddress('');
        setBankInfoReceived(false);
        openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
      }
    } else {
      // if numbers are incomplete, clear any previously fetched bank info
      setBankName('');
      setBranchAddress('');
      setBankInfoReceived(false);
    }
  };

  useEffect(() => {
    if (!isSimulation) fetchBankInfo();

  }, [routingNumber, branchTransitNumber]);

  const handleNumberInput = (setter, maxLength) => (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= maxLength) {
      setter(value);
      setIsFormTouched(true);
    }
  };

  // form validity includes parent validationErrors (should be empty)
  const localFieldsValid = () => {
    return (
      accountNumber.trim().length >= 7 &&
      accountNumber.trim().length <= 12 &&
      routingNumber.trim().length === 3 &&
      branchTransitNumber.trim().length === 5 &&
      dateOfWithdraw.trim() !== ''
     // bankInfoReceived
    );
  };

  // exposed methods for parent
  useImperativeHandle(ref, () => ({
    isFormValid: () => {
      const hasValidationErrors = validationErrors && Object.keys(validationErrors).length > 0;
      return localFieldsValid() && !hasValidationErrors;
    },

    // used by the parent when pressing pay button
    handlePayNow: async () => {
      setIsFormTouched(true);

      if (!localFieldsValid()) {
        // show error feedback inside EFT component if local fields invalid
        return false;
      }

      setLoading(true);
      try {
        const apiUrl = `${ApiConstants.BASE_URL}${ApiConstants.getPaymentEFT(product, productId, client)}`;
        const paymentData = {
          day: parseInt(dateOfWithdraw, 10),
          banking: {
            branchNumber: branchTransitNumber,
            institutionNumber: routingNumber,
            accountNumber: accountNumber,
          },
        };

        const response = await authenticatedApiFetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentData),
        });

        if (response?.status === 200 && response?.data === true) {
          return true;
        } else {
          openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
          return false;
        }
      } catch (err) {
        console.error('EFT payment error:', err);
        openErrorModal(ERROR_TITLE, ERROR_MESSAGE);
        return false;
      } finally {
        if (isMounted.current) setLoading(false);
      }
    }
  }), [accountNumber, routingNumber, branchTransitNumber, dateOfWithdraw, bankInfoReceived, validationErrors, authenticatedApiFetch, product, productId, client, language]);

  const bankInfo = (e) => {
    e.preventDefault();
    const BankInfoImage = language === 'Français' ? BankInfoImageFr : BankInfoImageEn;
    const title = language === 'Français' ? 'Informations sur le compte bancaire' : 'Bank Account Info';
    openBankModal(title, `<img src="${BankInfoImage}" alt="Bank Info" width={672} height={295} />`);
  };

  // UI classes for invalid fields
  const accountClass = isFormTouched && (accountNumber.trim().length < 7 || accountNumber.trim().length > 12) ? 'is-invalid' : '';
  const routingClass = isFormTouched && routingNumber.trim().length !== 3 ? 'is-invalid' : '';
  const branchClass = isFormTouched && branchTransitNumber.trim().length !== 5 ? 'is-invalid' : '';
  const dateClass = isFormTouched && !dateOfWithdraw ? 'is-invalid' : '';
  // bankInfoReceived shows if we've got valid bank info
  const bankInfoClass = '';

  return (
    <div className='payment'>
      <div className='paymentEFT'>
        {/* Account Number + Institution Number */}
        <div className='payment-EFT-bank'>
          <div className='entry'>
            <label htmlFor="accountNumber">{t('account_number')}</label>
            <a href="#" className="question-icon" onClick={bankInfo}>
              <i className="fa-solid fa-circle-question"></i>
            </a>
            <Modal isVisible={isBankModalVisible} title={bankModalContent.title} content={bankModalContent.content} onClose={closeBankModal} />
            <div className='form-group'>
              <input
                type="text"
                className={`form-control ${accountClass}`}
                id="accountNumber"
                value={accountNumber}
                onChange={handleNumberInput(setAccountNumber, 12)}
                disabled={isSimulation}
              />
              {/*accountClass && <div className="invalid-feedback">{t('please_enter_a_valid_account_number')}</div>*/}
            </div>
          </div>

          <div className='entry'>
            <label htmlFor="routingNumber">{t('banking_institution_number')}</label>
            <a href="#" className="question-icon" onClick={bankInfo}>
              <i className="fa-solid fa-circle-question"></i>
            </a>
            <div className='form-group'>
              <input
                type="text"
                className={`form-control ${routingClass}`}
                id="routingNumber"
                value={routingNumber}
                onChange={handleNumberInput(setRoutingNumber, 3)}
                disabled={isSimulation}
              />
              {/*routingClass && <div className="invalid-feedback">{t('please_enter_a_valid_institution_number')}</div>*/}
            </div>
          </div>
        </div>

        {/* Branch + Date */}
        <div className='payment-EFT-bank'>
          <div className='entry'>
            <label htmlFor="branchTransitNumber">{t('branch_transit_number')}</label>
            <a href="#" className="question-icon" onClick={bankInfo}>
              <i className="fa-solid fa-circle-question"></i>
            </a>
            <div className='form-group'>
              <input
                type="text"
                className={`form-control ${branchClass}`}
                id="branchTransitNumber"
                value={branchTransitNumber}
                onChange={handleNumberInput(setBranchTransitNumber, 5)}
                disabled={isSimulation}
              />
              {/*branchClass && <div className="invalid-feedback">{t('please_enter_a_valid_branch_number')}</div>*/}
            </div>
          </div>

          <div className='entry ordinal-numbers'>
            <label htmlFor="dateOfWithdraw">
              {t('date_of_withdrawal')} (1{language === 'Français' ? <span>er</span> : <span>st</span>}
              {language === 'English' ? ' to ' : ' au '}28
              {language === 'Français' ? ' du mois' : <span>th</span>})
            </label>
            <div className='form-group'>
              <div className='drop'>
                <select
                  id="dateOfWithdraw"
                  className={`form-control ${dateClass}`}
                  value={dateOfWithdraw}
                  onChange={(e) => { setDateOfWithdraw(e.target.value); setIsFormTouched(true); }}
                  disabled={isSimulation}
                >
                  <option value="">{t('select_date')}</option>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
                {/*dateClass && <div className="invalid-feedback">{t('please_select_a_date')}</div>*/}
              </div>
            </div>
          </div>
        </div>

        {/* Name of Bank */}
        <div className='entry'>
          <label htmlFor="bankName">{t('name_of_bank')}</label>
          <div className='form-group'>
            <input type="text" className={`form-control ${bankInfoClass}`} id="bankName" value={bankName} readOnly />
            {/*bankInfoClass && <div className="invalid-feedback">{t('bank_info_not_found_or_incomplete')}</div>*/}
          </div>
        </div>

        {/* Branch Address */}
        <div className='entry'>
          <label htmlFor="branchAddress">{t('branch_address')}</label>
          <div className='form-group'>
            <textarea id="branchAddress" value={branchAddress} readOnly rows="2" className="form-control" />
          </div>
        </div>

        <ErrorModal isVisible={isErrorModalVisible} onClose={closeErrorModal} />
      </div>
    </div>
  );
});

export default PaymentEFT;
